import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Container,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  Paper,
  Box,
  FormControl,
  InputLabel,
  Avatar,
  Stack,
  InputAdornment,
  Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import NotesIcon from '@mui/icons-material/Notes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import dataService from '../services/dataService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { UserContext } from '../UserContext';

const EditProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.userId;
  const { user } = useContext(UserContext);
  const resolvedUserId = userId || user?.userId || user?.id;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    bio: '',
    account_type: 0,
  });

  useEffect(() => {
    const loadUser = async () => {
      try {
        let data = await dataService.loadUserData(userId);
        setProfile({
          name: data.name,
          email: data.email,
          bio: data.bio || '',
          account_type: data.account_type,
        });
        setLoading(false);
      } catch (error) {
        console.log(error);
        setError('Failed to load profile');
        setLoading(false);
      }
    };
    if (userId !== undefined) loadUser();
  }, [userId]);

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await dataService.modifyUserData(userId, profile);
      navigate('/dashboard');
    } catch (error) {
      console.log(error);
      setError('Failed to save changes');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading profile...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'primary.main',
              mx: 'auto',
              mb: 2,
            }}
          >
            <PersonIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" fontWeight={700}>
            Edit Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update your personal information
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel id="account-type-label">Account Type</InputLabel>
              <Select
                labelId="account-type-label"
                id="account_type"
                name="account_type"
                value={profile.account_type}
                label="Account Type"
                onChange={handleChange}
                startAdornment={
                  <InputAdornment position="start">
                    <BadgeIcon color="action" />
                  </InputAdornment>
                }
              >
                <MenuItem value={0}>Normal User</MenuItem>
                <MenuItem value={2}>Merchant</MenuItem>
                <MenuItem value={3}>Waiter</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              id="name"
              name="name"
              label="Name"
              value={profile.name}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email Address"
              type="email"
              value={profile.email}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              id="bio"
              name="bio"
              label="Bio"
              value={profile.bio}
              onChange={handleChange}
              multiline
              rows={4}
              placeholder="Tell us about yourself and your food preferences..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                    <NotesIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/dashboard')}
                sx={{ flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving}
                sx={{ flex: 1 }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditProfile;
